import {
  CachedEntry,
  deriveTacticLabel,
  FallbackFamily,
  GENERIC_FALLBACK_COPY,
} from "@/lib/freeTier";
import { Shield } from "lucide-react";
import RehearsalPreview from "./RehearsalPreview";
import { mapFreePreviewPattern } from "@/lib/previewRehearsalRequest";

export type FreeAnalysisTier = "exact" | "fuzzy" | "weak" | "generic";

interface Props {
  message: string;
  entry: CachedEntry | null;
  matchType: FreeAnalysisTier;
  family?: FallbackFamily | null;
  displayLabel?: string | null;
  articulation?: string | null;
  remaining: number; // after this analysis is counted
}

const scoreColorHsl = (score: number): string => {
  if (score >= 7) return "hsl(var(--drama-high))";
  if (score >= 4) return "hsl(var(--drama-mid))";
  return "hsl(210 15% 65%)";
};

const FreeAnalysisView = ({ message, entry, matchType, family, displayLabel, articulation, remaining }: Props) => {

  // ---- Header (analyzed message) — shared across all tiers ----
  const Header = (
    <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-4 md:p-5">
      <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono mb-2">
        Analyzed Message
      </p>
      <p className="text-[15px] md:text-base text-white/90 leading-relaxed italic">
        "{message}"
      </p>
    </div>
  );

  const Counter = (
    <p className="text-center text-[11px] font-mono uppercase tracking-[0.25em] text-white/35">
      {remaining <= 0
        ? "That was your last free analysis"
        : remaining === 1
          ? "1 free analysis left"
          : `${remaining} free analyses left`}
    </p>
  );

  // ---- TIER 3: Generic fallback (no label, no score) ----
  if (matchType === "generic") {
    return (
      <div className="w-full max-w-2xl mx-auto space-y-6">
        {Header}
        <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-5 md:p-6">
          <p className="text-[15px] md:text-[16px] text-white/85 leading-relaxed">
            {GENERIC_FALLBACK_COPY}
          </p>
        </div>
        {Counter}
      </div>
    );
  }

  // ---- TIER 2: Weak match — variant fallback (soft label, no score, NO entry access) ----
  if (matchType === "weak") {
    const label = displayLabel || "Pattern Signal";
    const body = articulation || GENERIC_FALLBACK_COPY;
    return (
      <div className="w-full max-w-2xl mx-auto space-y-6">
        {Header}
        <div className="flex justify-center">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md font-mono text-[11px] uppercase tracking-wider border border-cyan-400/25 text-cyan-200/85 bg-cyan-400/[0.04]">
            <Shield className="w-3 h-3" />
            This resembles: {label}
          </span>
        </div>
        <div className="rounded-xl border border-cyan-400/20 bg-cyan-400/[0.03] p-5 md:p-6">
          <p className="text-[15px] md:text-[16px] text-white/85 leading-relaxed">
            {body}
          </p>
        </div>
        <p className="text-xs text-muted-foreground text-center">
          One message isn't proof. Patterns show up through repetition.
        </p>
        <RehearsalPreview key={message} primaryPattern={mapFreePreviewPattern(label)} previewVariant={label} />
        {Counter}
      </div>
    );
  }

  // ---- TIER 1: exact / fuzzy — original render with score ring + tactic ----
  if (!entry) return null;
  const score = Number(entry.toxicity_score) || 0;
  const tactic = deriveTacticLabel(entry);
  const color = scoreColorHsl(score);
  const ringDash = 2 * Math.PI * 72;

  return (
    <div className="w-full max-w-2xl mx-auto space-y-6">
      {Header}

      {matchType === "fuzzy" && (
        <p className="text-[11px] font-mono text-cyan-300/70 tracking-wider text-center -mt-2">
          MATCHED TO CLOSEST PATTERN IN OUR LIBRARY
        </p>
      )}

      <div className="flex flex-col items-center gap-3">
        <div className="relative w-32 h-32 md:w-40 md:h-40 flex items-center justify-center">
          <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 160 160">
            <circle cx="80" cy="80" r="72" fill="none" stroke="hsl(0 0% 15%)" strokeWidth="6" />
            <circle
              cx="80" cy="80" r="72" fill="none"
              stroke={color}
              strokeWidth="6"
              strokeLinecap="round"
              strokeDasharray={`${ringDash}`}
              strokeDashoffset={`${ringDash * (1 - score / 10)}`}
              style={{ filter: `drop-shadow(0 0 8px ${color})` }}
            />
          </svg>
          <div className="relative flex flex-col items-center z-10">
            <span className="text-[10px] uppercase tracking-[0.15em] text-white/50 font-mono">
              Threat Level
            </span>
            <span className="text-5xl md:text-6xl font-bold tabular-nums font-mono" style={{ color }}>
              {score.toFixed(1)}
            </span>
            <span className="text-xs font-mono text-white/40">/ 10</span>
          </div>
        </div>

        <span
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md font-mono text-xs font-bold uppercase tracking-wider border"
          style={{ color, borderColor: `${color.replace(")", " / 0.35)")}`, background: `${color.replace(")", " / 0.08)")}` }}
        >
          <Shield className="w-3 h-3" />
          {tactic}
        </span>
      </div>

      {entry.teaser_text && (
        <p className="text-[13px] md:text-sm text-white/60 italic font-mono text-center max-w-md mx-auto leading-relaxed">
          "{entry.teaser_text}"
        </p>
      )}

      {entry.analysis_logic && (
        <div className="rounded-xl border border-cyan-400/20 bg-cyan-400/[0.03] p-4 md:p-5">
          <p className="text-[10px] uppercase tracking-[0.2em] text-cyan-300 font-mono font-semibold mb-2">
            Underlying Tactic
          </p>
          <p className="text-sm md:text-[15px] text-white/80 leading-relaxed">
            {entry.analysis_logic}
          </p>
        </div>
      )}

      <p className="text-xs text-muted-foreground text-center">
        One message isn't proof. Patterns show up through repetition.
      </p>
      <RehearsalPreview key={message} primaryPattern={mapFreePreviewPattern(tactic)} />
      {Counter}
    </div>
  );
};

export default FreeAnalysisView;
